import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Send, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { conciergeChat } from "../lib/concierge.functions";
import { SectionLabel } from "../components/SectionLabel";
import { KMark } from "../components/KMark";
const mascotVideo = "/mascot.mp4";

type Msg = { role: "user" | "assistant"; content: string };

const SUGGESTIONS = [
  "What's the lightest gluten-free option that pairs with a dry white wine?",
  "I'm vegetarian and love truffle — what would you recommend?",
  "Something rich for a celebration, please.",
  "What's on the dessert menu tonight?",
];

export const Route = createFileRoute("/demo")({
  head: () => ({
    meta: [
      { title: "Live Demo — thatKorner" },
      {
        name: "description",
        content:
          "Try the world's first Native Ai dining concierge live. Ask Chef Buddy about dishes, allergens and pairings — in real time.",
      },
      { property: "og:title", content: "thatKorner — Live Concierge Demo" },
      {
        property: "og:description",
        content: "Talk to Chef Buddy, the digital concierge.",
      },
      { property: "og:url", content: "https://www.thatkorner.com/demo" },
    ],
    links: [{ rel: "canonical", href: "https://www.thatkorner.com/demo" }],
  }),
  component: DemoPage,
});

function DemoPage() {
  const [messages, setMessages] = useState<Msg[]>([
    {
      role: "assistant",
      content:
        "Welcome to That Korner. I'm Chef Buddy — your concierge for the evening. What are you in the mood for?",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 99999, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || loading) return;
    const next: Msg[] = [...messages, { role: "user", content: trimmed }];
    setMessages(next);
    setInput("");
    setLoading(true);
    try {
      const res = await conciergeChat({ data: { messages: next } });
      setMessages([...next, { role: "assistant", content: res.reply }]);
    } catch {
      setMessages([
        ...next,
        { role: "assistant", content: "Apologies — please try again." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-ink px-5 py-24 md:px-10 md:py-32">
      <div className="mx-auto grid max-w-[1480px] gap-12 md:grid-cols-[1fr_1.3fr] md:items-start">
        <div className="md:sticky md:top-28">
          <SectionLabel>Live Demo</SectionLabel>
          <h1 className="h-display mt-6 text-[clamp(2.25rem,5vw,4.25rem)] text-bone">
            Meet <span className="text-ember">ChefBuddy</span> — your digital concierge.
          </h1>
          <p className="mt-6 max-w-md text-bone/70">
            This is a live mock-up of the That Korner table. Ask anything a guest
            might ask — allergens, pairings, recommendations. Chef Buddy answers in
            real-time, in the brand's voice.
          </p>
          <div className="relative mt-8 hidden h-56 w-56 md:block">
            <div
              aria-hidden
              className="absolute inset-[-25%] animate-pulse rounded-full blur-3xl"
              style={{
                background:
                  "radial-gradient(circle at 50% 50%, rgba(240,31,14,0.55) 0%, rgba(232,147,32,0.32) 35%, rgba(245,239,230,0.10) 60%, rgba(10,10,10,0) 80%)",
              }}
            />
            <div className="relative h-full w-full overflow-hidden rounded-full bg-[#dadada] ring-2 ring-ember/40 shadow-[0_30px_60px_-10px_rgba(255,45,22,0.45)]">
              <video
                src={mascotVideo}
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                aria-label="Chef Buddy the digital concierge"
                className="h-full w-full object-cover [transform:scale(1.08)]"
              >
                <track kind="captions" src="/mascot-captions.vtt" srcLang="en" label="English" default />
              </video>
            </div>
          </div>
        </div>

        {/* Chat surface */}
        <div className="flex h-[640px] flex-col overflow-hidden rounded-3xl border border-bone/10 bg-bone/[0.02]">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-bone/10 bg-ember/95 px-5 py-3 text-ink">
            <div className="flex items-center gap-3">
              <KMark tone="ink" className="h-6 w-6" />
              <div>
                <p className="font-display text-sm font-bold">That Korner · Table 12</p>
                <p className="text-xs text-ink/70">Chef Buddy, your concierge</p>
              </div>
            </div>
            <span className="flex items-center gap-2 text-xs">
              <span className="h-2 w-2 animate-spark rounded-full bg-ink" /> Live
            </span>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto p-5">
            <AnimatePresence initial={false}>
              {messages.map((m, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-ember text-ink"
                        : "border border-bone/10 bg-bone/[0.04] text-bone"
                    }`}
                  >
                    {m.content}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
            {loading && (
              <div className="flex items-center gap-2 text-bone/70">
                <Sparkles className="h-4 w-4 animate-spark text-ember" />
                <span className="text-xs">Chef Buddy is composing…</span>
              </div>
            )}
          </div>

          {/* Suggestions */}
          {messages.length <= 1 && (
            <div className="flex flex-wrap gap-2 px-5 pb-3">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  className="rounded-full border border-bone/15 bg-bone/[0.03] px-3 py-1.5 text-xs text-bone/80 transition-colors hover:border-ember hover:text-bone"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send(input);
            }}
            className="flex items-center gap-2 border-t border-bone/10 bg-ink p-3"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask Chef Buddy anything…"
              disabled={loading}
              className="flex-1 rounded-full border border-bone/15 bg-bone/[0.03] px-5 py-3 text-sm text-bone placeholder:text-bone/70 focus:border-ember focus:outline-none"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="flex h-11 w-11 items-center justify-center rounded-full bg-ember text-bone transition-colors hover:bg-ember/90 disabled:opacity-40"
              aria-label="Send"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
